import { useEffect, useId, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import PresenterPanel from "../components/PresenterPanel";
import api, { fileUrl, errMsg } from "../lib/api";
import { toast, NoticeRegion } from "../lib/notices";
import { Loader2, Sparkles, Save, GripVertical } from "lucide-react";
import { track } from "../lib/analytics";
import { useConfig } from "../lib/config";

export default function Storyboard() {
  const { featureEnabled } = useConfig();
  const { id } = useParams();
  const [proj, setProj] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => {
      setProj(r.data);
      const sb = r.data.storyboard?.scenes || r.data.storyboard_scenes || [];
      setScenes(sb);
    }).catch(() => {});
  }, [id]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/projects/${id}/storyboard/generate`);
      setScenes(r.data.scenes || []);
      track("storyboard_generated", { project_id: id });
      toast.success("Storyboard generated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${id}/storyboard`, { scenes });
      toast.success("Storyboard saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const updateScene = (idx, field, value) => {
    setScenes((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const screenshots = proj?.screenshots || [];
  // One id prefix for the page; scene index makes each control unique.
  const uid = useId();
  const fieldId = (idx, field) => `${uid}-scene-${idx}-${field}`;

  return (
    <div className="min-h-screen">
      <Header />
      <main id="main" tabIndex={-1} className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <NoticeRegion />
        <Link to={`/project/${id}`} className="text-xs uppercase tracking-widest text-ink-muted hover:text-ink-accent">← Back to project</Link>
        <div className="flex flex-wrap justify-between gap-4 mt-4 mb-8">
          <div>
            <div className="marquee-tag mb-2">// STORYBOARD</div>
            <h1 className="font-display font-black type-title tracking-tighter">{proj?.title || "Storyboard"}</h1>
          </div>
          <div className="flex gap-2">
            {scenes.length > 0 && (
              <button type="button" onClick={save} disabled={saving} className="brutal-btn brutal-btn-secondary">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
              </button>
            )}
            <button type="button" onClick={generate} disabled={generating} className="brutal-btn">
              {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {scenes.length ? "Regenerate" : "Generate"}
            </button>
          </div>
        </div>

        {scenes.length === 0 && (
          <div className="brutal-card p-12 text-center text-ink-muted">
            <p className="mb-6">Generate a scene-by-scene storyboard from your script and screenshots.</p>
            <button type="button" onClick={generate} className="brutal-btn">Generate Storyboard</button>
          </div>
        )}

        <div className="space-y-4">
          {scenes.map((scene, idx) => (
            <div key={idx} className="brutal-card p-6 grid md:grid-cols-12 gap-6">
              <div className="md:col-span-1 flex items-start pt-2 text-ink-muted">
                <GripVertical size={18} aria-hidden="true" />
                <h2 className="font-display font-black type-heading ml-1">
                  <span className="sr-only">Scene </span>{scene.scene_number || idx + 1}
                </h2>
              </div>
              <div className="md:col-span-3">
                {screenshots[scene.screenshot_index] ? (
                  <img src={fileUrl(screenshots[scene.screenshot_index])} alt="" className="w-full aspect-video object-cover brutal-border" />
                ) : (
                  <div className="aspect-video bg-surface brutal-border flex items-center justify-center text-xs text-ink-subtle">No image</div>
                )}
              </div>
              <div className="md:col-span-8 space-y-3">
                {/* A placeholder is not a label: it disappears on first
                    keystroke and several of these are never announced at
                    all. Real labels, visually hidden to keep the layout. */}
                <label className="sr-only" htmlFor={fieldId(idx, "title")}>Scene {idx + 1} title</label>
                <input id={fieldId(idx, "title")} className="brutal-input w-full font-display font-bold" value={scene.title || ""} onChange={(e) => updateScene(idx, "title", e.target.value)} placeholder="Scene title" />
                <label className="sr-only" htmlFor={fieldId(idx, "voiceover")}>Scene {idx + 1} voiceover</label>
                <textarea id={fieldId(idx, "voiceover")} className="brutal-input w-full text-sm min-h-[60px]" value={scene.voiceover || ""} onChange={(e) => updateScene(idx, "voiceover", e.target.value)} placeholder="Voiceover" />
                <label className="sr-only" htmlFor={fieldId(idx, "on-screen-text")}>Scene {idx + 1} on-screen text</label>
                <input id={fieldId(idx, "on-screen-text")} className="brutal-input w-full text-sm" value={scene.on_screen_text || ""} onChange={(e) => updateScene(idx, "on_screen_text", e.target.value)} placeholder="On-screen text" />
                <div className="flex gap-4 text-xs">
                  <label className="flex items-center gap-2">
                    Duration
                    <input type="number" className="brutal-input w-16 py-1" value={scene.duration_seconds || 5} onChange={(e) => updateScene(idx, "duration_seconds", parseFloat(e.target.value))} />
                    s
                  </label>
                  <label className="sr-only" htmlFor={fieldId(idx, "transition")}>Scene {idx + 1} transition</label>
                  <select id={fieldId(idx, "transition")} className="brutal-select text-xs" value={scene.transition || "fade"} onChange={(e) => updateScene(idx, "transition", e.target.value)}>
                    <option value="cut">Cut</option>
                    <option value="fade">Fade</option>
                    <option value="slide">Slide</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        {proj && featureEnabled("avatar_presenter") && (
          <div className="mt-4">
            <PresenterPanel
              projectId={proj.project_id}
              presenter={proj.presenter}
              language={proj.language}
              onChange={(presenter) => setProj((p) => ({ ...p, presenter }))}
            />
          </div>
        )}
      </main>
    </div>
  );
}
